import { z } from "zod";
const traceIdentifierSchema = z.string().trim().min(1).max(240);
const traceReasonCodeSchema = z.string()
    .regex(/^[a-z][a-z0-9_]{0,79}$/u)
    .max(80);
const jsonPointerSchema = z.string()
    .max(500)
    .regex(/^(?:\/(?:[^~/]|~[01])*)+$/u, "Expected a non-root JSON pointer");
const sha256Schema = z.string().regex(/^[0-9a-f]{64}$/u);
export const memoryTraceLaneSchema = z.enum(["episodic", "intentional"]);
export const memoryTraceSourceSchema = z.enum([
    "preliminary_scout",
    "memory_serving_episodic",
    "search_episodic_memory",
    "memory_digest",
    "load_memory_context",
]);
/** The control-plane-resolved scope used for the memory decision. */
export const memoryTraceScopeSchema = z.object({
    tenantSlug: traceIdentifierSchema,
    organisationId: traceIdentifierSchema.nullable(),
    actorId: traceIdentifierSchema,
    channelId: traceIdentifierSchema.nullable(),
}).strict();
export const episodicSegmentMemoryTraceRefSchema = z.object({
    kind: z.literal("episodic"),
    segmentId: traceIdentifierSchema,
    sourceId: traceIdentifierSchema,
    sessionId: traceIdentifierSchema,
}).strict();
/**
 * Canonical Episode v1 reference. `sourceSessionId` is nullable because audit
 * evidence can originate outside a session; eligible serving still applies
 * the stricter organisation, actor, and current-session exclusion policy.
 */
export const episodicEpisodeMemoryTraceRefSchema = z.object({
    kind: z.literal("episodic"),
    representation: z.literal("episode"),
    episodeId: traceIdentifierSchema,
    sourceRunId: traceIdentifierSchema,
    sourceSessionId: traceIdentifierSchema.nullable(),
}).strict();
// Keep the segment form readable while Canonical Episode v1 replaces the
// legacy segment corpus. New serving receipts use the explicit episode form.
export const episodicMemoryTraceRefSchema = z.union([
    episodicSegmentMemoryTraceRefSchema,
    episodicEpisodeMemoryTraceRefSchema,
]);
export const intentionalMemoryTraceRefSchema = z.object({
    kind: z.literal("intentional"),
    memoryId: traceIdentifierSchema,
    version: z.number().int().positive().safe(),
}).strict();
export const memoryTraceRefSchema = z.union([
    episodicMemoryTraceRefSchema,
    intentionalMemoryTraceRefSchema,
]);
export const memoryTraceReasonSchema = z.object({
    code: traceReasonCodeSchema,
    message: z.string().trim().min(1).max(500),
}).strict();
const memoryTraceRefsSchema = z.array(memoryTraceRefSchema).max(100);
const nonEmptyMemoryTraceRefsSchema = memoryTraceRefsSchema.min(1);
export const memoryTraceRetrievalCandidateSchema = z.object({
    memoryRef: memoryTraceRefSchema,
    arms: z.array(z.enum([
        "exact",
        "semantic",
        "temporal",
        "scope",
        "key",
        "entity",
        "lexical",
    ])).min(1).max(7),
    score: z.number().finite().optional(),
    exactRank: z.number().int().positive().nullable().optional(),
    semanticRank: z.number().int().positive().nullable().optional(),
    temporalRank: z.number().int().positive().nullable().optional(),
}).strict();
const retrievalResultSchema = z.discriminatedUnion("outcome", [
    z.object({
        outcome: z.literal("retrieved"),
        retrievalMode: z.enum([
            "exact",
            "semantic",
            "hybrid",
            "exact_fallback",
            "scoped",
            "lexical",
        ]),
        candidateCount: z.number().int().nonnegative().safe(),
        candidates: z.array(memoryTraceRetrievalCandidateSchema).max(100),
        indexGenerationId: traceIdentifierSchema.nullable(),
        indexSourceWatermark: z.number().finite().nullable(),
        servingAttemptId: traceIdentifierSchema.optional(),
        asOf: z.number().int().nonnegative().safe().optional(),
    }).strict(),
    z.object({
        outcome: z.literal("failed"),
        reason: memoryTraceReasonSchema,
    }).strict(),
]);
export const memoryTraceRetrievalPayloadSchema = z.object({
    phase: z.literal("retrieval"),
    result: retrievalResultSchema,
}).strict();
const authorisationResultSchema = z.discriminatedUnion("outcome", [
    z.object({
        outcome: z.literal("authorised"),
        memoryRefs: memoryTraceRefsSchema,
        candidateCount: z.number().int().nonnegative().safe(),
    }).strict(),
    z.object({
        outcome: z.literal("rejected"),
        candidateCount: z.number().int().nonnegative().safe(),
        reason: memoryTraceReasonSchema,
    }).strict(),
]);
export const memoryTraceAuthorisationPayloadSchema = z.object({
    phase: z.literal("authorisation"),
    result: authorisationResultSchema,
}).strict();
const admissionResultSchema = z.discriminatedUnion("outcome", [
    z.object({
        outcome: z.literal("admitted"),
        memoryRefs: nonEmptyMemoryTraceRefsSchema,
    }).strict(),
    z.object({
        outcome: z.literal("rejected"),
        memoryRefs: nonEmptyMemoryTraceRefsSchema,
        reason: memoryTraceReasonSchema,
    }).strict(),
    z.object({
        outcome: z.literal("not_evaluated"),
        memoryRefs: nonEmptyMemoryTraceRefsSchema,
        reason: memoryTraceReasonSchema,
    }).strict(),
]);
export const memoryTraceAdmissionPayloadSchema = z.object({
    phase: z.literal("admission"),
    result: admissionResultSchema,
}).strict();
export const memoryTraceSurfaceTargetSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("context"),
        contextId: traceIdentifierSchema,
        section: z.enum([
            "preliminary_scout",
            "canonical_episode_evidence",
            "memory_digest",
            "memory_lifecycle_targets",
        ]),
    }).strict(),
    z.object({
        kind: z.literal("tool"),
        toolName: z.enum(["search_episodic_memory", "load_memory_context"]),
        toolCallId: traceIdentifierSchema,
    }).strict(),
]);
const surfaceResultSchema = z.discriminatedUnion("outcome", [
    z.object({
        outcome: z.literal("surfaced"),
        memoryRefs: nonEmptyMemoryTraceRefsSchema,
        target: memoryTraceSurfaceTargetSchema,
    }).strict(),
    z.object({
        outcome: z.literal("omitted"),
        memoryRefs: nonEmptyMemoryTraceRefsSchema,
        target: memoryTraceSurfaceTargetSchema,
        reason: memoryTraceReasonSchema,
    }).strict(),
]);
export const memoryTraceSurfacePayloadSchema = z.object({
    phase: z.literal("surface"),
    result: surfaceResultSchema,
}).strict();
export const memoryTraceApplicationTargetSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("planning_field"),
        planId: traceIdentifierSchema,
        fieldPath: jsonPointerSchema,
    }).strict(),
    z.object({
        kind: z.literal("tool_argument"),
        toolName: traceIdentifierSchema,
        toolCallId: traceIdentifierSchema,
        argumentPath: jsonPointerSchema,
    }).strict(),
    z.object({
        kind: z.literal("sql_clause"),
        statementId: traceIdentifierSchema,
        clause: z.enum([
            "select",
            "join",
            "where",
            "group_by",
            "having",
            "order_by",
            "limit",
        ]),
        expressionSha256: sha256Schema,
    }).strict(),
]);
export const memoryTraceApplicationEvidenceSchema = z.object({
    memoryRef: memoryTraceRefSchema,
    target: memoryTraceApplicationTargetSchema,
}).strict();
export const memoryTraceApplicationPayloadSchema = z.object({
    phase: z.literal("application"),
    result: z.object({
        outcome: z.literal("applied"),
        targetEvidence: z.array(memoryTraceApplicationEvidenceSchema).min(1).max(100),
    }).strict(),
}).strict();
export const memoryTracePayloadSchema = z.discriminatedUnion("phase", [
    memoryTraceRetrievalPayloadSchema,
    memoryTraceAuthorisationPayloadSchema,
    memoryTraceAdmissionPayloadSchema,
    memoryTraceSurfacePayloadSchema,
    memoryTraceApplicationPayloadSchema,
]);
function payloadMemoryRefs(payload) {
    switch (payload.phase) {
        case "retrieval":
            return payload.result.outcome === "failed"
                ? []
                : payload.result.candidates.map((candidate) => candidate.memoryRef);
        case "authorisation":
            return payload.result.outcome === "rejected" ? [] : payload.result.memoryRefs;
        case "admission":
        case "surface":
            return payload.result.memoryRefs;
        case "application":
            return payload.result.targetEvidence.map((evidence) => evidence.memoryRef);
    }
}
export const memoryTraceEventSchema = z.object({
    schemaVersion: z.literal(1),
    traceId: traceIdentifierSchema,
    operationId: traceIdentifierSchema,
    lane: memoryTraceLaneSchema,
    source: memoryTraceSourceSchema,
    scope: memoryTraceScopeSchema,
    payload: memoryTracePayloadSchema,
}).strict().superRefine((event, context) => {
    const expectedLane = event.source === "preliminary_scout" ||
        event.source === "memory_serving_episodic" ||
        event.source === "search_episodic_memory"
        ? "episodic"
        : "intentional";
    if (event.lane !== expectedLane) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["lane"],
            message: `${event.source} events belong to the ${expectedLane} lane`,
        });
    }
    for (const [index, memoryRef] of payloadMemoryRefs(event.payload).entries()) {
        if (memoryRef.kind === event.lane)
            continue;
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["payload", "result", "memoryRefs", index],
            message: `${event.lane} events may only carry ${event.lane} memory references`,
        });
    }
    if (event.payload.phase === "retrieval" &&
        event.payload.result.outcome === "retrieved" &&
        event.payload.result.candidateCount < event.payload.result.candidates.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["payload", "result", "candidateCount"],
            message: "Candidate count cannot be smaller than the stable candidate references",
        });
    }
    if (event.payload.phase === "authorisation" &&
        event.payload.result.outcome === "authorised" &&
        event.payload.result.candidateCount < event.payload.result.memoryRefs.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["payload", "result", "candidateCount"],
            message: "Candidate count cannot be smaller than the authorised references",
        });
    }
    if (event.payload.phase === "application") {
        for (const [index, evidence] of event.payload.result.targetEvidence.entries()) {
            if (evidence.target.kind !== "tool_argument" ||
                evidence.target.toolCallId === event.operationId) {
                continue;
            }
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["payload", "result", "targetEvidence", index, "target", "toolCallId"],
                message: "Tool-argument application evidence must correlate to the trace operation",
            });
        }
        return;
    }
    if (event.payload.phase !== "surface")
        return;
    const target = event.payload.result.target;
    const expectedContextSection = event.source === "memory_serving_episodic"
        ? "canonical_episode_evidence"
        : event.source;
    const targetMatchesSource = target.kind === "context"
        ? target.section === expectedContextSection ||
            (event.source === "memory_digest" && target.section === "memory_lifecycle_targets")
        : target.toolName === event.source;
    if (!targetMatchesSource) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["payload", "result", "target"],
            message: "Surface target must identify the exact source context or tool",
        });
    }
    const targetMatchesOperation = target.kind === "tool"
        ? target.toolCallId === event.operationId
        : target.contextId === event.traceId;
    if (!targetMatchesOperation) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["payload", "result", "target"],
            message: "Surface target must correlate to the trace operation",
        });
    }
});
